import React, { FC, useContext, useState, useEffect, useMemo } from "react";
import { Box, Group, Text } from "@mantine/core";
import { CompetitionContext } from "../../../CompetitionContext.tsx";
import { TeamLogo } from "../../../Components/TeamLogo.tsx";
import { useDebouncedCurrentGame } from "../../../hooks/useDebouncedCurrentGame.ts";
import { useTeam } from "../../../hooks/useTeam.ts";
import { usePlayerListByTeam } from "../../../hooks/usePlayerListByTeam.ts";
import { components } from "../../../openapi/generated-types.ts";

type Player = components["schemas"]["Player"];

const SWITCH_INTERVAL_MS = 10_000;

const getLuminance = (hex: string): number => {
  const color = hex.replace("#", "");
  const r = parseInt(color.substring(0, 2), 16) / 255;
  const g = parseInt(color.substring(2, 4), 16) / 255;
  const b = parseInt(color.substring(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

const lightenColor = (hex: string, amount: number): string => {
  const color = hex.replace("#", "");
  const r = Math.min(255, parseInt(color.substring(0, 2), 16) + amount);
  const g = Math.min(255, parseInt(color.substring(2, 4), 16) + amount);
  const b = Math.min(255, parseInt(color.substring(4, 6), 16) + amount);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

const getColors = (rawColor: string) => {
  const luminance = getLuminance(rawColor);
  const isDark = luminance < 0.2;
  const isLight = luminance > 0.6;

  return {
    stripeColor: isDark ? lightenColor(rawColor, 80) : rawColor,
    boxColor: rawColor,
    boxBorder: isDark ? "2px solid #666" : "none",
    numberColor: isLight ? "#000000" : "#ffffff",
  };
};

export const SquadFullscreen: FC = () => {
  const competitionId = useContext(CompetitionContext);
  const [showHome, setShowHome] = useState(true);

  const currentGame = useDebouncedCurrentGame(competitionId);
  const homeTeamId = currentGame.data?.actualTeams.homeTeamId;
  const guestTeamId = currentGame.data?.actualTeams.guestTeamId;

  const homeTeam = useTeam(homeTeamId);
  const guestTeam = useTeam(guestTeamId);

  const homePlayers = usePlayerListByTeam(homeTeamId);
  const guestPlayers = usePlayerListByTeam(guestTeamId);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowHome((prev) => !prev);
    }, SWITCH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  const currentTeam = showHome ? homeTeam.data : guestTeam.data;
  const currentPlayers = showHome ? homePlayers.data : guestPlayers.data;
  const rawTeamColor = currentTeam?.primaryColor ?? "#666666";

  const { stripeColor, boxColor, boxBorder, numberColor } = useMemo(
    () => getColors(rawTeamColor),
    [rawTeamColor]
  );

  const sortedPlayers = useMemo(
    () => (currentPlayers ? [...currentPlayers].sort((a, b) => a.number - b.number) : []),
    [currentPlayers]
  );

  if (
    !currentGame.isSuccess ||
    !homeTeam.isSuccess ||
    !guestTeam.isSuccess ||
    !homePlayers.isSuccess ||
    !guestPlayers.isSuccess
  ) {
    return <></>;
  }

  return (
    <Box
      style={{
        backgroundColor: "black",
        width: 1920,
        height: 1080,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Top stripe */}
      <Box style={{ height: 20, backgroundColor: stripeColor, flexShrink: 0 }} />

      {/* Header */}
      <Box
        px={60}
        py={40}
        style={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 40,
        }}
      >
        <Box style={{ flexShrink: 0, width: 160, height: 160 }}>
          <TeamLogo teamId={currentTeam?.id} height={160} />
        </Box>
        <Text size="90px" c="white" fw={900} style={{ lineHeight: 1 }}>
          {currentTeam?.name}
        </Text>
      </Box>

      {/* Player list */}
      <Box
        px={60}
        mt={40}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: 0,
        }}
      >
        <PlayerGrid
          players={sortedPlayers}
          boxColor={boxColor}
          boxBorder={boxBorder}
          numberColor={numberColor}
        />
      </Box>

      {/* Bottom stripe */}
      <Box style={{ height: 20, backgroundColor: stripeColor, flexShrink: 0 }} />
    </Box>
  );
};

const PlayerGrid: FC<{
  players: Player[];
  boxColor: string;
  boxBorder: string;
  numberColor: string;
}> = ({ players, boxColor, boxBorder, numberColor }) => {
  const numColumns = 3;
  const maxRows = 10;
  const maxPlayers = numColumns * maxRows;
  const displayPlayers = players.slice(0, maxPlayers);

  const columns: Player[][] = [[], [], []];
  displayPlayers.forEach((player, index) => {
    columns[index % numColumns]?.push(player);
  });

  const maxColumnLength = Math.max(...columns.map((c) => c.length), 1);
  const fontSize = Math.min(48, Math.floor(650 / maxColumnLength));

  return (
    <Group align="flex-start" gap={40} style={{ width: "100%" }}>
      {columns.map((column, colIndex) => (
        <Box key={colIndex} style={{ flex: 1 }}>
          {column.map((player) => (
            <PlayerRow
              key={player.id}
              player={player}
              boxColor={boxColor}
              boxBorder={boxBorder}
              numberColor={numberColor}
              fontSize={fontSize}
            />
          ))}
        </Box>
      ))}
    </Group>
  );
};

const PlayerRow: FC<{
  player: Player;
  boxColor: string;
  boxBorder: string;
  numberColor: string;
  fontSize: number;
}> = ({ player, boxColor, boxBorder, numberColor, fontSize }) => {
  const abbreviatedFirstName = player.firstName.charAt(0) + ".";
  const displayName = `${abbreviatedFirstName} ${player.lastName}`;
  const boxSize = fontSize + 20;

  return (
    <Group gap={20} py={12}>
      <Box
        style={{
          width: boxSize,
          height: boxSize,
          backgroundColor: boxColor,
          border: boxBorder,
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Text size={`${fontSize - 6}px`} c={numberColor} fw={900}>
          {player.number}
        </Text>
      </Box>
      <Text size={`${fontSize}px`} c="white" fw={600}>
        {displayName}
      </Text>
    </Group>
  );
};
